import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, saveFile, type Company, type LedgerReport, type VatRegisters } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount as written, date, money, period as writtenPeriod } from '../format';
import * as Icon from '../components/Icons';
import { JpkSubmissions } from '../components/JpkSubmissions';
import { ErrorAlert, failureOf, type Failure } from '../components/Alerts';
import { BadDebtPanel } from '../components/BadDebt';
import { VatUePanel } from '../components/VatUe';
import type { Panel } from '../components/Shell';

const currentMonth = () => new Date().toISOString().slice(0, 7);

/** Suma kolumn kosztowych KPiR (10–13); kolumna 7 to przychód. */
function costsOf(report: LedgerReport): number {
  return Object.entries(report.byKpirColumn)
    .filter(([column]) => Number(column) >= 10)
    .reduce((sum, [, value]) => sum + Number(value), 0);
}

export function LedgerPanel({
  company,
  t,
  locale,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  onGo?: (panel: Panel) => void;
}) {
  const [month, setMonth] = useState(currentMonth);
  const [report, setReport] = useState<LedgerReport | null>(null);
  const [vat, setVat] = useState<VatRegisters | null>(null);
  const [vatNote, setVatNote] = useState<string | null>(null);
  const [error, setError] = useState<Failure | null>(null);
  const [jpkBusy, setJpkBusy] = useState(false);
  const [jpkError, setJpkError] = useState<string | null>(null);

  async function downloadJpk() {
    setJpkBusy(true);
    setJpkError(null);
    try {
      saveFile(await api.jpkV7m(company.id, month));
    } catch (failure) {
      setJpkError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setJpkBusy(false);
    }
  }

  const load = useCallback(async () => {
    setError(null);
    const [year, monthNumber] = month.split('-').map(Number);
    const from = `${month}-01`;
    const to = new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10);

    try {
      setReport(await api.ledger(company.id, from, to));
    } catch (failure) {
      setError(failureOf(failure));
    }

    // Rejestrów VAT nie ma firma zwolniona — to nie błąd, tylko informacja
    try {
      setVat(await api.vatRegisters(company.id, month));
      setVatNote(null);
    } catch (failure) {
      setVat(null);
      setVatNote(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const revenue = Number(report?.totalRevenue ?? 0);
  const costs = report ? costsOf(report) : 0;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.ledger.title}</h3>
          <p>{writtenPeriod(month, locale)}</p>
        </div>
        <div className="sp">
          <input
            className="inp w"
            type="month"
            aria-label={t.ledger.month}
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>
      </div>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      <div className="split">
        <div className="panel metric">
          <span className="k">{t.ledger.revenue}</span>
          <span className="v num">{money(revenue, locale)}</span>
          <span className="sub">{t.ledger.revenueColumn}</span>
        </div>
        <div className="panel metric">
          <span className="k">{t.ledger.costsTotal}</span>
          <span className="v num">{money(costs, locale)}</span>
        </div>
        <div className="panel metric">
          <span className="k">{t.ledger.result}</span>
          <span className="v num">{money(revenue - costs, locale)}</span>
        </div>
        {vat && (
          <div className="panel metric lead">
            {/* Ujemny wynik to nadwyżka podatku naliczonego, czyli kwota do przeniesienia */}
            <span className="k">{Number(vat.vatResult) >= 0 ? t.ledger.vatToPay : t.ledger.vatToCarry}</span>
            <span className="v num">{money(Math.abs(Number(vat.vatResult)), locale)}</span>
            <span className="sub">
              {t.ledger.vatDue} {money(vat.vatDue, locale)} − {t.ledger.vatDeductible}{' '}
              {money(vat.vatDeductible, locale)}
            </span>
          </div>
        )}
      </div>

      <div className="panel">
        <header>
          <h4>{t.ledger.entries}</h4>
          <div className="sp">
            <span className="sm muted">{t.ledger.columnsNote}</span>
          </div>
        </header>

        {report?.entries.length === 0 ? (
          <div className="empty">
            <b>{t.ledger.none}</b>
          </div>
        ) : (
          report && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th className="n">{t.ledger.ordinal}</th>
                    <th>{t.ledger.date}</th>
                    <th>{t.ledger.document}</th>
                    <th>{t.ledger.counterparty}</th>
                    <th>{t.ledger.description}</th>
                    <th className="n">{t.ledger.column}</th>
                    <th className="n">{t.ledger.revenue}</th>
                    <th className="n">{t.ledger.costsTotal}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.entries.map((entry, index) => {
                    // Kolumna 7 to przychód, 10–13 to koszty — wiersz księgi ma obie kolumny,
                    // a nie jedną „kwotę”, bo inaczej nie widać, po której stronie stoi zapis
                    const isCost = (entry.kpirColumn ?? 0) >= 10;
                    return (
                      <tr key={entry.id}>
                        <td className="n">{index + 1}</td>
                        <td className="doc">{date(entry.entryDate, locale)}</td>
                        <td className="doc">{entry.documentNumber ?? '—'}</td>
                        <td>{entry.counterpartyName ?? '—'}</td>
                        <td>{entry.description}</td>
                        <td className="n">{entry.kpirColumn ?? '—'}</td>
                        <td className="n">{isCost ? '—' : written(entry.amount, locale)}</td>
                        <td className="n">{isCost ? written(entry.amount, locale) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6}>
                      {t.invoices.totals} · {writtenPeriod(month, locale)}
                    </td>
                    <td className="n">{written(revenue, locale)}</td>
                    <td className="n">{written(costs, locale)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )
        )}
      </div>

      <div className="grid two">
        <div className="panel">
          <header>
            <h4>{t.ledger.vatTitle}</h4>
          </header>
          {vatNote && <p className="muted sm">{vatNote}</p>}
          {vat && (
            <dl className="brk">
              <dt>{t.ledger.vatDue}</dt>
              <dd>{money(vat.vatDue, locale)}</dd>
              <dt>{t.ledger.vatDeductible}</dt>
              <dd>{money(vat.vatDeductible, locale)}</dd>
              <dt className="tot">{Number(vat.vatResult) >= 0 ? t.ledger.vatToPay : t.ledger.vatToCarry}</dt>
              <dd className="tot">{money(Math.abs(Number(vat.vatResult)), locale)}</dd>
            </dl>
          )}
          {vat?.quarterly && (
            <div className="alert a-info">
              <Icon.Info />
              <div>
                <b>{t.ledger.quarterly}</b>
              </div>
            </div>
          )}
        </div>

        {/* Plik JPK ma sens tylko tam, gdzie są rejestry VAT — firma zwolniona go nie składa */}
        {vat && (
          <div className="panel">
            <header>
              <h4>{t.jpk.title}</h4>
            </header>
            {/* Przy rozliczeniu kwartalnym powstaje inny plik i wchodzi do niego co innego — piszemy to */}
            <p className="sm muted">{vat.quarterly ? t.jpk.leadQuarterly : t.jpk.lead}</p>
            <div className="alert a-info">
              <Icon.Info />
              <div>
                <b>{t.jpk.beforeDownload}</b>
              </div>
            </div>
            {jpkError && (
              <div className="alert a-bad">
                <Icon.Error />
                <div>
                  <b>{jpkError}</b>
                </div>
              </div>
            )}
            <div className="row">
              <button type="button" className="btn" onClick={() => void downloadJpk()} disabled={jpkBusy}>
                <Icon.Download />
                {vat.quarterly ? t.jpk.downloadQuarterly : t.jpk.download}
              </button>
              {onGo && (
                <button type="button" className="btn ghost" onClick={() => onGo('settings')}>
                  {t.jpk.goToFiling}
                </button>
              )}
            </div>
            <JpkSubmissions company={company} month={month} t={t} locale={locale} />
          </div>
        )}
      </div>

      {/*
        Ulga na złe długi stoi przy rejestrach i przy pliku, bo to ich dotyczy: korekta z art. 89b
        wchodzi do ewidencji zakupu tego okresu. Firma zwolniona z VAT jej nie ma — tak samo jak
        rejestrów.
      */}
      {/* Informacja VAT-UE stoi przy rejestrach: te same dokumenty, ten sam miesiąc, inny obowiązek */}
      {vat && (
        <VatUePanel
          company={company}
          month={month}
          t={t}
          locale={locale}
          editable={!company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER')}
          onGo={onGo}
        />
      )}

      {vat && (
        <BadDebtPanel
          company={company}
          month={month}
          t={t}
          locale={locale}
          editable={!company.roles.every((role) => role === 'VIEWER' || role === 'BOARD_MEMBER')}
          onGo={onGo}
        />
      )}
    </>
  );
}
